import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Alert,
  IconButton, Divider, CircularProgress, Avatar, CardHeader,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowCircleDownOutlinedIcon from "@mui/icons-material/ArrowCircleDownOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TableRowsIcon from "@mui/icons-material/TableRows";
import { PieChart, Pie, Cell, Tooltip as RechartTooltip } from "recharts";
import * as XLSX from "xlsx";
import { axiosPrivate } from "../../api/axios";
import PageBody from "../GeneralComponents/PageBody";
import InputFilter from "../GeneralComponents/InputFilter";

const DIAS_ES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const WEEK_DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const MESES_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function percColor(perc) {
  if (perc == null) return "#9E9E9E";
  if (perc >= 95) return "#4CAF50";
  if (perc >= 70) return "#FFA726";
  return "#EF5350";
}

function PercBadge({ value }) {
  if (value == null) return null;
  const pct = Math.round(value);
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      bgcolor: percColor(value), borderRadius: "10px", px: 0.8, py: 0.15, minWidth: 36,
    }}>
      <Typography variant="caption" sx={{ color: "white", fontWeight: "bold", fontSize: "0.68rem", lineHeight: 1.4 }}>
        {pct}%
      </Typography>
    </Box>
  );
}

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getWeekDates(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((day + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const nd = new Date(monday);
    nd.setDate(monday.getDate() + i);
    return toDateStr(nd);
  });
}

export default function RecepcionIntegridad() {
  const today = new Date();
  const currentYear  = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  const availableYears = useMemo(() => {
    const years = [];
    for (let y = 2025; y <= currentYear; y++) years.push(y);
    return years;
  }, [currentYear]);

  const [selectedYear,    setSelectedYear]    = useState(currentYear);
  const [selectedMonth,   setSelectedMonth]   = useState(currentMonth);
  const [selectedOperador, setSelectedOperador] = useState(null);
  const [selectedDate,    setSelectedDate]    = useState(toDateStr(today));

  const [operators, setOperators] = useState([]);
  const [apiData,   setApiData]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);

  const availableMonthsForYear = useMemo(() => {
    const months = [];
    for (let m = 1; m <= 12; m++) {
      if (selectedYear < currentYear || m <= currentMonth) months.push(m);
    }
    return months;
  }, [selectedYear, currentYear, currentMonth]);

  // Load operators once
  useEffect(() => {
    axiosPrivate({ method: "GET", url: "/service/admin/get_operadores/", showSnackbarOnError: false })
      .then((res) => {
        const ops = res.data ?? [];
        setOperators(ops);
        if (ops.length > 0) setSelectedOperador(ops[0].id_operador);
      })
      .catch(() => {});
  }, []);

  // Fetch stats when operator / year / month changes
  useEffect(() => {
    if (!selectedOperador) return;
    const mm          = String(selectedMonth).padStart(2, "0");
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const fechaInicio = `${selectedYear}-${mm}-01`;
    const fechaFin    = `${selectedYear}-${mm}-${String(daysInMonth).padStart(2, "0")}`;

    setLoading(true);
    setError(null);
    axiosPrivate({
      method: "GET",
      url: "/service/reportes/stats-rbd/agregado",
      params: {
        granularidad: "1d",
        dimension: "operador",
        fecha_inicio: fechaInicio,
        fecha_fin:    fechaFin,
        id_operador:  selectedOperador,
      },
      showSnackbarOnError: false,
    })
      .then((res) => setApiData((res.data ?? []).map((r) => ({ ...r, fecha: r.fecha_bucket }))))
      .catch(() => setError("Error al cargar los datos."))
      .finally(() => setLoading(false));
  }, [selectedOperador, selectedYear, selectedMonth]);

  const periodDates = useMemo(
    () => [...new Set(apiData.map((r) => r.fecha))].sort(),
    [apiData]
  );

  useEffect(() => {
    if (periodDates.length > 0 && !periodDates.includes(selectedDate)) {
      setSelectedDate(periodDates[periodDates.length - 1]);
    }
  }, [periodDates]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveDate = useCallback(
    (dir) => {
      const idx  = periodDates.indexOf(selectedDate);
      const next = idx + dir;
      if (next >= 0 && next < periodDates.length) setSelectedDate(periodDates[next]);
    },
    [periodDates, selectedDate]
  );

  const dayData = useMemo(() => {
    const rows = apiData.filter((r) => r.fecha === selectedDate);
    if (!rows.length) return null;
    const sum = (key) => rows.reduce((a, r) => a + (r[key] ?? 0), 0);
    const total     = sum("slots_dia_total");
    const reportados = sum("slots_dia_reportados");
    const faltantes  = sum("slots_dia_faltantes");
    const invalidos  = sum("slots_dia_invalidos");
    const correctos  = reportados - invalidos;
    return {
      total, reportados, faltantes, invalidos, correctos,
      percRecepcion:  total     > 0 ? (reportados / total)      * 100 : null,
      percIntegridad: reportados > 0 ? (correctos  / reportados) * 100 : null,
      rbdsConectados: sum("n_rbd"),
      rbdsReportando: sum("n_rbd"),
    };
  }, [apiData, selectedDate]);

  const weekDates = useMemo(() => getWeekDates(selectedDate), [selectedDate]);

  const weekStats = useMemo(() =>
    weekDates.map((d) => {
      const rows = apiData.filter((r) => r.fecha === d);
      if (!rows.length) return { fecha: d, percRecepcion: null, percIntegridad: null };
      const total = rows.reduce((a, r) => a + (r.slots_dia_total    ?? 0), 0);
      const rep   = rows.reduce((a, r) => a + (r.slots_dia_reportados ?? 0), 0);
      const inv   = rows.reduce((a, r) => a + (r.slots_dia_invalidos  ?? 0), 0);
      return {
        fecha: d,
        percRecepcion:  total > 0 ? (rep / total)         * 100 : null,
        percIntegridad: rep   > 0 ? ((rep - inv) / rep)   * 100 : null,
      };
    }),
    [weekDates, apiData]
  );

  const opInfo  = operators.find((o) => o.id_operador === selectedOperador);
  const opName  = opInfo?.nombre_operador || "";
  const opAbbr  = opName.split(" ").map((w) => w[0]).join("").slice(0, 3).toUpperCase();

  const donutData = dayData
    ? [
        { name: "Correctos",  value: dayData.correctos, color: "#4CAF50" },
        { name: "Incorrectos", value: dayData.invalidos,  color: "#EF5350" },
      ]
    : [];

  const handleDownload = () => {
    const rows = apiData
      .filter((r) => r.fecha === selectedDate)
      .map((r) => ({
        fecha:              r.fecha,
        operador:           opName,
        n_rbd:              r.n_rbd,
        slots_total:        r.slots_dia_total,
        slots_reportados:   r.slots_dia_reportados,
        slots_faltantes:    r.slots_dia_faltantes,
        slots_invalidos:    r.slots_dia_invalidos,
        cumplimiento_dia:   r.cumplimiento_dia,
      }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Recepción");
    XLSX.writeFile(wb, `recepcion_integridad_${selectedDate}.xlsx`);
  };

  const selDateObj = new Date(selectedDate + "T12:00:00");
  const selDayName = DIAS_ES[selDateObj.getDay()];
  const dateIdx    = periodDates.indexOf(selectedDate);

  return (
    <PageBody>
      {/* Title */}
      <Typography
        variant="h5" fontWeight="bold" textAlign="center" gutterBottom
        sx={{ letterSpacing: 1, textTransform: "uppercase", mb: 3 }}
      >
        Recepción de Datos e Integridad
      </Typography>

      {/* Filters row */}
      <Grid2 container spacing={2} mb={3}>
        <Grid2 size={4}>
          <InputFilter
            item="id_operador"
            name="Operador"
            opt={operators.map((o) => ({ value: String(o.id_operador), label: o.nombre_operador }))}
            value={String(selectedOperador ?? "")}
            callback={(_, val) => { if (val != null) setSelectedOperador(Number(val)); }}
          />
        </Grid2>
        <Grid2 size={4}>
          <InputFilter
            item="year"
            name="Año"
            icon={<CalendarTodayIcon />}
            opt={availableYears.map((y) => ({ value: String(y), label: String(y) }))}
            value={String(selectedYear)}
            callback={(_, val) => { if (val != null) setSelectedYear(Number(val)); }}
            sortDesc
          />
        </Grid2>
        <Grid2 size={4}>
          <InputFilter
            item="month"
            name="Mes"
            icon={<CalendarTodayIcon />}
            opt={availableMonthsForYear.map((m) => ({ value: String(m), label: MESES_ES[m - 1] }))}
            value={String(selectedMonth)}
            callback={(_, val) => { if (val != null) setSelectedMonth(Number(val)); }}
            noSort
          />
        </Grid2>
      </Grid2>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Un solo Paper para todo el contenido */}
      <Paper sx={{ mb: 2 }}>

        {/* Cabecera operador */}
        <CardHeader
          sx={{ borderBottom: "1px solid #0000001F" }}
          avatar={
            <Avatar sx={{ bgcolor: "#757575", borderRadius: 2, width: 40, height: 40 }}>
              <Typography variant="caption" fontWeight="bold" color="white">{opAbbr}</Typography>
            </Avatar>
          }
          title={
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">{opName}</Typography>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 2, mt: 0.3, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <FiberManualRecordIcon sx={{ color: "#4CAF50", fontSize: 12 }} />
                  <Typography variant="body2" color="text.secondary">
                    Estado de servicio Conectado: <strong>{dayData?.rbdsConectados ?? 0}</strong>
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <TableRowsIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography variant="body2" color="text.secondary">
                    Recepción de Datos: <strong>{dayData?.rbdsReportando ?? 0}</strong>
                  </Typography>
                </Box>
              </Box>
            </Box>
          }
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 1, mt: 1 }}>
              <IconButton
                size="small"
                disableRipple
                onClick={() => dateIdx > 0 && moveDate(-1)}
                sx={{
                  border: "1px solid #1976D2",
                  borderRadius: 1,
                  color: "#1976D2",
                  opacity: dateIdx <= 0 ? 0.35 : 1,
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 170, textAlign: "center" }}>
                {selDayName} / {selectedDate}
              </Typography>
              <IconButton
                size="small"
                disableRipple
                onClick={() => dateIdx < periodDates.length - 1 && moveDate(1)}
                sx={{
                  border: "1px solid #1976D2",
                  borderRadius: 1,
                  color: "#1976D2",
                  opacity: dateIdx >= periodDates.length - 1 ? 0.35 : 1,
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          }
        />

        {/* Resumen semanal */}
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" mb={1.5}>Resumen semanal</Typography>
          <Box sx={{
            display: "grid", gridTemplateColumns: "repeat(7, 1fr)",
            border: "1px solid #0000001F", borderRadius: 1, overflow: "hidden",
          }}>
            {weekStats.map(({ fecha, percRecepcion, percIntegridad }, i) => {
              const isSelected = fecha === selectedDate;
              return (
                <Box
                  key={fecha}
                  onClick={() => setSelectedDate(fecha)}
                  sx={{
                    cursor: "pointer",
                    borderRight: i < 6 ? "1px solid #0000001F" : "none",
                    "&:hover": { bgcolor: isSelected ? undefined : "#f5f9ff" },
                  }}
                >
                  <Box sx={{
                    p: 1, textAlign: "center",
                    bgcolor: isSelected ? "#1976D2" : "#fafafa",
                    borderBottom: "1px solid #0000001F",
                  }}>
                    <Typography variant="caption" fontWeight="bold" display="block"
                      sx={{ color: isSelected ? "white" : "text.primary" }}>
                      {WEEK_DAYS[i]}
                    </Typography>
                    <Typography variant="caption" display="block"
                      sx={{ color: isSelected ? "rgba(255,255,255,0.85)" : "text.secondary", fontSize: "0.67rem" }}>
                      {fecha}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1, minHeight: 68 }}>
                    {percRecepcion == null ? (
                      <Typography variant="caption" color="text.secondary" display="block"
                        textAlign="center" sx={{ mt: 1.5, fontSize: "0.68rem" }}>
                        Sin datos aún
                      </Typography>
                    ) : (
                      <>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.6 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.67rem" }}>
                            Recepción de datos
                          </Typography>
                          <PercBadge value={percRecepcion} />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.67rem" }}>
                            Integridad de datos
                          </Typography>
                          <PercBadge value={percIntegridad} />
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {loading ? (
          <Box textAlign="center" p={4}><CircularProgress /></Box>
        ) : !dayData ? (
          <Box textAlign="center" p={4}>
            <Typography color="text.secondary">Sin datos para el día seleccionado.</Typography>
          </Box>
        ) : (
          <>
            <Divider />

            {/* Recepción + Integridad */}
            <Box sx={{ display: "flex" }}>
              {/* Recepción de Datos */}
              <Box sx={{ flex: 1, p: 3, display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" fontWeight="bold" textAlign="center" gutterBottom>
                  RECEPCIÓN DE DATOS
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
                  ¿Cuanto llegó de lo esperado?
                </Typography>

                <Box sx={{
                  bgcolor: percColor(dayData.percRecepcion),
                  borderRadius: 2, p: 3, textAlign: "center", mb: 2,
                  mx: "auto", width: "65%",
                }}>
                  <Typography variant="h2" fontWeight="bold" sx={{ color: "white", lineHeight: 1.1 }}>
                    {dayData.percRecepcion?.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", mt: 0.5 }}>
                    de recepción
                  </Typography>
                </Box>

                <Box sx={{ position: "relative", height: 30, borderRadius: 1, overflow: "hidden", bgcolor: "#E0E0E0", mb: 1 }}>
                  <Box sx={{
                    position: "absolute", left: 0, top: 0, bottom: 0,
                    width: `${dayData.percRecepcion ?? 0}%`,
                    bgcolor: percColor(dayData.percRecepcion),
                    display: "flex", alignItems: "center", justifyContent: "center",
                    minWidth: 36,
                  }}>
                    <Typography variant="caption" color="white" fontWeight="bold">
                      {dayData.percRecepcion?.toFixed(0)}%
                    </Typography>
                  </Box>
                  {(100 - (dayData.percRecepcion ?? 0)) > 4 && (
                    <Box sx={{ position: "absolute", right: 8, top: 0, bottom: 0, display: "flex", alignItems: "center" }}>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary">
                        {(100 - (dayData.percRecepcion ?? 0)).toFixed(0)}%
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: "flex", justifyContent: "center", gap: 2.5, mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: percColor(dayData.percRecepcion) }} />
                    <Typography variant="caption">Recibidos</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#E0E0E0" }} />
                    <Typography variant="caption">No recibidos</Typography>
                  </Box>
                </Box>

                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: "flex" }}>
                  <Box sx={{ flex: 1, textAlign: "center", py: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                      <ArrowCircleDownOutlinedIcon sx={{ color: "text.secondary", fontSize: 26 }} />
                      <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                        {dayData.total.toLocaleString("es-CL")}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">Esperados</Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ flex: 1, textAlign: "center", py: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                      <CheckCircleOutlineIcon sx={{ color: "text.secondary", fontSize: 26 }} />
                      <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                        {dayData.reportados.toLocaleString("es-CL")}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">Recibidos</Typography>
                  </Box>
                </Box>
              </Box>

              <Divider orientation="vertical" flexItem />

              {/* Integridad de Datos */}
              <Box sx={{ flex: 1, p: 3, display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" fontWeight="bold" textAlign="center" gutterBottom>
                  INTEGRIDAD DE DATOS
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" mb={1} sx={{ minHeight: 40 }}>
                  De los datos que sí llegaron... ¿cuántos están correctos e incorrectos?
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, flex: 1, my: 1 }}>
                  <PieChart width={190} height={190}>
                    <Pie
                      data={donutData} cx={95} cy={95}
                      innerRadius={52} outerRadius={88}
                      dataKey="value" startAngle={90} endAngle={-270}
                    >
                      {donutData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartTooltip formatter={(v) => v.toLocaleString("es-CL")} />
                  </PieChart>
                  <Box>
                    {donutData.map((d) => (
                      <Box key={d.name} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2.5 }}>
                        <Box sx={{ width: 14, height: 14, bgcolor: d.color, borderRadius: 0.5, mt: 0.5, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="h6" fontWeight="bold" sx={{ color: d.color, lineHeight: 1.2 }}>
                            {dayData.reportados > 0
                              ? ((d.value / dayData.reportados) * 100).toFixed(1)
                              : 0}%
                          </Typography>
                          <Typography variant="body2">{d.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {d.value.toLocaleString("es-CL")}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                  *Los datos pueden cargarse con un delay o demora de hasta 24hrs.
                </Typography>
              </Box>
            </Box>

            <Divider />

            {/* Distribución sobre el total esperado */}
            <Grid2 container alignItems="center" spacing={2} sx={{ p: 2.5 }}>
              <Grid2 size={3}>
                <Typography variant="subtitle2" fontWeight="bold" textTransform="uppercase" sx={{ lineHeight: 1.5 }}>
                  Distribución sobre el total esperado
                </Typography>
              </Grid2>
              <Grid2 size={9}>
                {dayData.total > 0 && (() => {
                  const pCorr = (dayData.correctos / dayData.total) * 100;
                  const pInc  = (dayData.invalidos  / dayData.total) * 100;
                  const pFalt = (dayData.faltantes  / dayData.total) * 100;
                  return (
                    <>
                      <Box sx={{ display: "flex", height: 30, borderRadius: 1, overflow: "hidden", mb: 1.5 }}>
                        {pCorr > 0 && (
                          <Box sx={{ width: `${pCorr}%`, bgcolor: "#4CAF50", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography variant="caption" color="white" fontWeight="bold">{pCorr.toFixed(0)}%</Typography>
                          </Box>
                        )}
                        {pInc > 0 && (
                          <Box sx={{ width: `${pInc}%`, bgcolor: "#EF5350", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography variant="caption" color="white" fontWeight="bold">{pInc.toFixed(0)}%</Typography>
                          </Box>
                        )}
                        {pFalt > 0 && (
                          <Box sx={{ flex: 1, bgcolor: "#9E9E9E", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography variant="caption" color="white" fontWeight="bold">{pFalt.toFixed(0)}%</Typography>
                          </Box>
                        )}
                      </Box>
                      <Grid2 container>
                        {[
                          { label: "Correctos",      count: dayData.correctos, color: "#4CAF50" },
                          { label: "Incorrectos",    count: dayData.invalidos,  color: "#EF5350" },
                          { label: "No recibidos",   count: dayData.faltantes,  color: "#9E9E9E" },
                          { label: "Total esperado", count: dayData.total,      color: null },
                        ].map((item) => (
                          <Grid2 key={item.label} size={3}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {item.color && (
                                <Box sx={{ width: 10, height: 10, bgcolor: item.color, borderRadius: 0.3, flexShrink: 0 }} />
                              )}
                              <Typography variant="caption" color={item.color ? "text.primary" : "text.secondary"}>
                                {item.label} ({item.count.toLocaleString("es-CL")})
                              </Typography>
                            </Box>
                          </Grid2>
                        ))}
                      </Grid2>
                    </>
                  );
                })()}
              </Grid2>
            </Grid2>
          </>
        )}

      </Paper>

      {/* Botón descarga */}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          onClick={handleDownload}
          sx={{
            border: "2px solid #003B8D",
            borderRadius: "4px",
            color: "#003B8D",
            backgroundColor: "#FFF",
            textTransform: "none",
            fontSize: "14px",
            padding: "8px 16px",
            fontWeight: 500,
            "&:hover": { backgroundColor: "#f0f4ff" },
          }}
        >
          Descargar detalle (.csv)
        </Button>
      </Box>
    </PageBody>
  );
}
